// SETO-7346: host-page accessibility helpers for the loader (focus, Escape, dialog attrs).
// Non-modal dialog semantics (aria-modal="false") — SETO-7080 keeps a floating overlay, not a
// page-locking modal, so we must not claim the rest of the page is inert.

const DIALOG_LABEL = 'Chat';

/** Snapshot the element that should receive focus again after the widget closes. */
export function rememberFocus(): Element | null {
  return document.activeElement;
}

/** Restore focus if the element is still an HTMLElement attached to the document. */
export function restoreFocus(el: Element | null): void {
  if (el instanceof HTMLElement && el.isConnected) {
    el.focus();
  }
}

/** Move focus to the visible widget surface (iframe or fallback). */
export function focusWidgetSurface(el: HTMLElement): void {
  el.focus();
}

/**
 * Toggle non-modal dialog naming on the open surface.
 * Cleared when closed so a 0x0 box is not announced as a dialog.
 */
export function setOpenDialogAttrs(el: HTMLElement, open: boolean): void {
  if (open) {
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'false');
    el.setAttribute('aria-label', DIALOG_LABEL);
    return;
  }

  el.removeAttribute('role');
  el.removeAttribute('aria-modal');
  el.removeAttribute('aria-label');
}

/**
 * Close the widget when Escape is pressed on the host page while it is open.
 * Cross-origin iframe keydowns are handled in web-chat-ui (webchat:close-request).
 * Returns an unbind function (init keeps the listener for the page lifetime today).
 */
export function bindEscapeToClose(getIsOpen: () => boolean, close: () => void): () => void {
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && getIsOpen()) {
      close();
    }
  };

  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}
